import Hero from '../components/Hero';
import SectionHeading from '../components/SectionHeading';
import CallToAction from '../components/CallToAction';
import { motion } from 'framer-motion';

const History = () => {
  const timelineEvents = [
    {
      year: "1987",
      title: "4th genetaion farmer",
      description: "Andy Frazier leaves the family farm in England to start the successful livestock grooming business, AFCS.",
      image: "/images/tenbury_show.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
    {
      year: "1993",
      title: "Company Sale",
      description: "Andy Frazier sells AFCS and joins the team at Ritchey Tagg, still supplying his product range and working intensely in the sheep identification sector.",
      image: "/images/smithfield_001.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
    {
      year: "1996",
      title: "Pedigree Flock requirements",
      description: "Andy starts 2 pedigree sheep flocks and looks around for some software to monitor and record them. When nothing suitable is found, he decides to write one.",
      image: "/images/nippy.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
    {
      year: "1997",
      title: "New Software",
      description: "Agriplan 2000 is launched with Andy Frazier as director. The company designs and develops the Pedigree Stock-minder software program and starts marketing to UK and European pedigree sheep and cattle breeders.",
      image: "/images/tractor.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
    {
      year: "2004",
      title: "UK Number One",
      description: "After 5 successful years with PSM, it becoming the market leader in pedigree software, Andy sells the business to Earlsmere ID. He then moves into working in the corporate sector for the next decade in software development, data and business analysis. Unfortunately he loses his Texel flock during the 2nd wave of the foot and mouth outbreak.",
      image: "/images/stars_judge.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
    {
      year: "2022",
      title: "New Requirements",
      description: "Now a successful author living partly in France with his wife, Wendy, Andy started a small flock of pedigree Ryeland sheep. He kept records on spreadsheets and found that many other small breeders were doing the same.  ",
      image: "/images/rhs2.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
    {
      year: "2024",
      title: "New Software",
      description: "With Andy’s development experience and Wendy’s corporate background, the pair decided to rewrite his original software in a new format, and share it with small breeders, for FREE.",
      image: "/images/awf2.jpg?auto=compress&cs=tinysrgb&w=1260&h=1260&dpr=2"
    },
    {
      year: "2025",
      title: "PedigreePro Launched",
      description: "PedigreePro software was launched in May 2025, initially to a small bunch of breeders who volunteered to trial it. So far it has been greatly received.",
      image: "/images/af1.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
  ];

  return (
    <>
      <Hero 
        title="Our Journey"
        subtitle="Learn about the history and evolution of PedigreePro - from its origins to becoming the leading sheep management software."
        image="/images/2004_menithwood_group.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />
      
      <section className="section bg-white">
        <div className="container">
          <SectionHeading 
            title="The PedigreePro Story"
            subtitle="From a small team of sheep enthusiasts to the revelation in simple sheep management software."
          />
          
          <motion.div 
            className="max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <p className="text-lg text-neutral-700 mb-6">
              PedigreePro was born out of necessity. As sheep breeders ourselves, we struggled with existing livestock management software that wasn't designed specifically for small sheep breeding operations. After years of using spreadsheets and paper records, we decided to build the solution we needed.
            </p>
            <p className="text-lg text-neutral-700">
              What started as a simple tool for our own use quickly gained interest from other breeders who faced the same challenges. We listened to their feedback, added features, and refined the system until it became the comprehensive platform it is today - trusted by lots of breeders worldwide. Most of all - we kept it simple.
            </p>
          </motion.div>
          
          <div className="space-y-16">
            {timelineEvents.map((event, index) => (
              <TimelineEvent 
                key={index}
                year={event.year}
                title={event.title}
                description={event.description}
                image={event.image}
                isEven={index % 2 === 0}
              />
            ))}
          </div>
        </div>
      </section>
      
      <section className="section bg-neutral-50">
        <div className="container">
          <SectionHeading 
            title="Our Mission and Values"
            subtitle="Guiding principles that have shaped PedigreePro since the beginning."
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            <ValueCard 
              title="Breeder-Focused"
              description="Everything we do is designed with the real needs of sheep breeders in mind. We are breeders ourselves and understand the challenges you face."
              delay={0.1}
            />
            
            <ValueCard 
              title="Keeping it simple"
              description="We are aware of other more complicated systems out there, but our mission is to keep it simple."
              delay={0.2}
            />
            
            <ValueCard 
              title="We are not alone"
              description="We are aware that there are 12,000 people in the UK who keep under 50 head of sheep. Many of those keep records on paper or spreadsheets. Worldwide, there are a lot more."
              delay={0.3}
            />
            
            <ValueCard 
              title="Food Security"
              description="In a modern world focussed on food security, it is more important than ever to keep accurate livestock records."
              delay={0.4}
            />
            
            <ValueCard 
              title="Community Support"
              description="We believe in fostering a community of sheep breeders who can learn from and support each other. PedigreePro is more than software; it's a community."
              delay={0.5}
            />
            
            <ValueCard 
              title="Environmental Stewardship"
              description="We're committed to supporting sustainable farming practices through better data management and breeding program optimization."
              delay={0.6}
            />
          </div>
        </div>
      </section>
      
      <section className="section bg-white">
        <div className="container">
          <SectionHeading 
            title="Meet the Team"
            subtitle="The passionate people behind PedigreePro who make it all possible."
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
            <TeamMember 
              name="Andy Frazier"
              role="Founder & CEO"
              bio="Sheep breeder for over 40 years and software engineer who combined his passions to create PedigreePro."
              image="/images/af1.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              delay={0.1}
            />
            
            <TeamMember 
              name="Wendy Frazier"
              role="Sheep enthusiast"
              bio="With a long career in corporate IT, combined with a passion for livestock, Wendy's focus is on the finer details."
              image="/images/wf1.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              delay={0.2}
            />
            
            <TeamMember 
              name="Still Room"
              role="UK Marketing"
              bio="We are looking for a smart, disciplined, motivated individual to help us spread the word of PedigreePro software across the UK sheep (and cattle) sector. If that is you, please get in touch. ."
              image="/images/you.jpg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              delay={0.3}
            />
            

          </div>
        </div>
      </section>
      
      <CallToAction 
        title="Be Part of Our Future"
        description="Join hundreds of sheep breeders who are using PedigreePro to transform their breeding programs."
        buttonText="Launch PedigreePro App"
        buttonLink="https://pedigreepro.netlify.app/"
        secondaryButtonText="View Subscription Plans"
        secondaryButtonLink="/subscription"
      />
    </>
  );
};

const TimelineEvent = ({ 
  year, 
  title, 
  description, 
  image,
  isEven 
}: { 
  year: string; 
  title: string; 
  description: string;
  image: string;
  isEven: boolean;
}) => (
  <motion.div 
    className={`grid md:grid-cols-5 items-center gap-4 md:gap-8 ${isEven ? '' : 'md:flex-row-reverse'}`}
    initial={{ opacity: 0, x: isEven ? -20 : 20 }}
    whileInView={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true, margin: "-100px" }}
  >
    <div className={`md:col-span-1 text-center ${isEven ? 'md:text-right' : 'md:text-left'} md:order-${isEven ? 1 : 2}`}>
      <span className="inline-block py-2 px-4 bg-primary text-white text-lg font-bold rounded-lg">
        {year}
      </span>
    </div>
    
    <div className={`relative h-full hidden md:block md:col-span-1 md:order-${isEven ? 2 : 1}`}>
      <div className="absolute top-0 bottom-0 left-1/2 transform -translate-x-1/2 w-1 bg-primary-light"></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-4 border-primary-light bg-white"></div>
    </div>
    
    <div className={`md:col-span-3 grid md:grid-cols-2 gap-6 bg-white p-6 rounded-xl shadow-md border border-neutral-200 md:order-${isEven ? 3 : 3}`}>
      <div>
        <h3 className="font-sans font-bold text-xl mb-2">{title}</h3>
        <p className="text-neutral-600">{description}</p>
      </div>
      <div className="aspect-video rounded-lg overflow-hidden order-first md:order-last">
        <img 
          src={image} 
          alt={title} 
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  </motion.div>
);

const ValueCard = ({ title, description, delay = 0 }: { title: string; description: string; delay?: number }) => (
  <motion.div 
    className="bg-white p-6 rounded-xl shadow-md border border-neutral-200"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true, margin: "-100px" }}
  >
    <h3 className="font-sans font-semibold text-xl mb-3">{title}</h3>
    <p className="text-neutral-600">{description}</p>
  </motion.div>
);

const TeamMember = ({ 
  name, 
  role, 
  bio, 
  image,
  delay = 0 
}: { 
  name: string; 
  role: string; 
  bio: string;
  image: string;
  delay?: number;
}) => (
  <motion.div 
    className="text-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    viewport={{ once: true, margin: "-100px" }}
  >
    <div className="rounded-full overflow-hidden w-40 h-40 mx-auto mb-4">
      <img 
        src={image} 
        alt={name} 
        className="w-full h-full object-cover"
      />
    </div>
    <h3 className="font-sans font-semibold text-xl">{name}</h3>
    <p className="text-primary font-medium mb-2">{role}</p>
    <p className="text-neutral-600">{bio}</p>
  </motion.div>
);

export default History;